/**
 * AEOApprovalPanel Component
 *
 * Panel for managing AEO content approval workflow.
 * Shows status, feedback summary, and approval/rejection controls.
 */

import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Send,
  User,
  Calendar,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { authFetch } from '@/utils/auth-fetch';
import { cn } from '@/lib/utils';

type ContentStatus = 'draft' | 'pending_review' | 'approved' | 'rejected';

interface ContentMetadata {
  id: string;
  status: ContentStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdByName?: string;
  approvedByName?: string;
  approvedAt?: string;
  rejectionReason?: string;
}

interface FeedbackSummary {
  thumbsUp: number;
  thumbsDown: number;
  comments: number;
  unresolved: number;
}

interface AEOApprovalPanelProps {
  contentId: string;
  metadata: ContentMetadata;
  feedbackSummary?: FeedbackSummary;
  onStatusChange?: (newStatus: ContentStatus) => void;
  onRegenerate?: () => void;
  className?: string;
}

const statusConfig: Record<ContentStatus, {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}> = {
  draft: {
    label: 'Draft',
    icon: <Clock className="h-4 w-4" />,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
  },
  pending_review: {
    label: 'Pending Review',
    icon: <Send className="h-4 w-4" />,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  approved: {
    label: 'Approved',
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  rejected: {
    label: 'Rejected',
    icon: <XCircle className="h-4 w-4" />,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
};

export function AEOApprovalPanel({
  contentId,
  metadata,
  feedbackSummary,
  onStatusChange,
  onRegenerate,
  className,
}: AEOApprovalPanelProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  // ⚠️ Kept with the other hooks ON PURPOSE. rules-of-hooks is UNENFORCED across admin/ — eslint is
  // installed and scripted but has no config, and no CI job runs lint (task-gxrz2a2j0i). A hook
  // declared further down, past a future early return, is the exact defect two reviewers had to
  // catch by reading on #4753. Declare hooks together at the top where that cannot happen.
  // ⛔⛔ SCOPED TO THE ACTION THAT PRODUCED IT, NOT A BARE STRING (codex minor, third round).
  //
  // A shared `actionError` with no owner is the actual defect; clear-on-open would only patch its
  // symptom. Unscoped, "Submit for Review" could fail, the operator opens Approve, and THE FRESH
  // CONFIRM DIALOG ALREADY CONTAINS THE SUBMIT ERROR BEFORE THEY HAVE ATTEMPTED ANYTHING.
  // ⇒ Scoping makes every future open/close path correct BY CONSTRUCTION rather than by someone
  //   remembering to clear — which is what the previous two rounds of this same defect cost.
  // 🔑 AND THE LESSON THIS COMPONENT KEEPS TEACHING: I fixed the four CLOSE paths, and closing every
  //   close path is exactly what made the OPEN path look handled. THE ENUMERATION WAS THE THING THAT
  //   WAS INCOMPLETE, not any member of it.
  const [actionError, setActionError] = useState<{ action: ContentStatus; message: string } | null>(null);

  const status = statusConfig[metadata.status];

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * ⛔ EVERY CLOSE PATH GOES THROUGH HERE (codex minor, second round).
   *
   * `onOpenChange` cleared actionError for outside-click and Escape, but the explicit Cancel
   * buttons called setShow*Dialog(false) directly and did NOT. ⇒ TWO OF THREE CLOSE PATHS SHARED
   * THE CORRECT HANDLING AND THE THIRD DID NOT — and the correct handling three lines away is
   * exactly what makes the gap read as deliberate rather than missed.
   * ⇒ A STALE ERROR THAT OUTLIVES THE ACTION IT DESCRIBES MAKES A SUCCEEDING SYSTEM LOOK BROKEN —
   *   the inverse of the defect this component was fixed for, introduced by the fix.
   */
  const closeDialogs = (which?: 'approve' | 'reject') => {
    if (which !== 'reject') setShowApproveDialog(false);
    if (which !== 'approve') setShowRejectDialog(false);
    setActionError(null);
  };

  /**
   * ⛔ EXEMPLAR FOR task-hv3nmzrer9 — READ THIS BEFORE COPYING IT TO THE OTHER 24 SITES.
   *
   * WHAT WAS WRONG. This handler was `if (response.ok) { …success… }` with NO `else`, and a `catch`
   * that only wrote to `console.error`. ⇒ On any non-ok response — 401 on an expired token, 403,
   * 409, 500 — NOTHING happened: the dialog stayed open, no message appeared, and the operator's
   * most likely reading was that the click did not register, so they pressed it again.
   * ⇒ A SILENT FALLBACK IN THE BANNED SENSE: the failure state was indistinguishable from
   * "nothing has happened yet".
   *
   * ⛔ AND IT THREW AWAY THE BACKEND'S OWN EXPLANATION. routes/business-about.js:1910 returns a
   * specific message on refusal. The whole argument for keeping backend refusals VERBATIM
   * (task-cohgo2fqx4) assumes the component RENDERS them; this one did not render them at all.
   *
   * ⇒ THE PATTERN, COPIED FROM admin/src/pages/VendorOpportunityPage.tsx:100 RATHER THAN INVENTED:
   * KEEP THE BODY AND THE STATUS TOGETHER. Every site in the swept class gives exactly that up —
   * they either read `.ok` and discard the body, or parse the body and never look at the status.
   * You need both: the status decides success, the body carries the reason.
   *
   * ⚠️ AND THE SURFACE IS AN INLINE ALERT, NOT A TOAST, DELIBERATELY. Measured 2026-08-03
   * (docs/reports/20260803-0441-persona-refusal-rendering-audit.md): this app's toast is
   * `max-w-[420px]` + `overflow-hidden` with no scroll, and its description has no
   * `whitespace-pre-line` — so a multi-line backend refusal loses its newlines AND is clipped at
   * both ends with no way to read the rest. Backend refusals in this repo are multi-line by design.
   * An inline alert is anchored to the action that failed, persists until the next attempt, grows
   * to fit, and `whitespace-pre-line` keeps the structure the backend author wrote.
   */
  const updateStatus = async (newStatus: ContentStatus, notes?: string) => {
    setIsLoading(true);
    setActionError(null);
    try {
      const response = await authFetch(`/category/aeo-content/${contentId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, notes }),
      });

      if (!response.ok) {
        // Body AND status, together. The body is read for its message even on failure; a response
        // that is not JSON at all (an nginx 502 returning HTML) must not turn into a parse error
        // that hides the real status, so fall back to the status line — explicitly, not silently.
        const body = await response.json().catch(() => null);
        // ⛔ WHICH STRING REACHES THE HUMAN IS A SEPARATE QUESTION FROM WHETHER ONE DOES (codex
        // minor). This read `body.error || body.message`, so on a backend that sends BOTH — a
        // machine code AND the human sentence, e.g. error:'migration_in_progress' with
        // message:'Schema migration pending, retry after deployment' — THE OPERATOR GOT THE TERSE
        // CODE AND THE EXPLANATION WAS DISCARDED. That is a smaller version of the defect this
        // component was fixed for: the message arrives and still does not say what to do.
        // ⇒ Render BOTH: the sentence leads because it is what they act on, the code follows
        //   because it is what they paste to us.
        const code = typeof body?.error === 'string' ? body.error.trim() : '';
        const sentence = typeof body?.message === 'string' ? body.message.trim() : '';
        throw new Error(
          (sentence && code && sentence !== code)
            ? `${sentence} (${code})`
            : (sentence || code || `Request failed (HTTP ${response.status})`)
        );
      }

      onStatusChange?.(newStatus);
      closeDialogs();
      setRejectionReason('');
    } catch (error: any) {
      // ⛔ The dialog stays OPEN and onStatusChange does NOT fire — the operator's work is still
      // in front of them and no caller is told a transition happened that did not.
      console.error('Error updating status:', error);
      setActionError({
        action: newStatus,
        message: error?.message || 'The status change failed and no reason was returned.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasUnresolvedIssues = feedbackSummary && feedbackSummary.unresolved > 0;

  /**
   * Rendered in the CARD **and** inside BOTH DIALOGS. On failure the dialog deliberately stays open,
   * and it covers the card — so an alert placed only in the card would be invisible at exactly the
   * moment it is needed. `whitespace-pre-line` because backend refusals in this repo are written
   * multi-line and the structure is load-bearing; `break-words` so a long category path cannot push
   * the message out of its container.
   */
  /**
   * The CARD alert reports whichever action last failed — it is the page-level record.
   * The DIALOG alerts are scoped: an Approve dialog can only ever show an Approve failure, so a
   * stale error from a different action cannot greet an operator who has just opened it.
   */
  const errorAlertFor = (action?: ContentStatus) => {
    if (!actionError) return null;
    if (action && actionError.action !== action) return null;
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="whitespace-pre-line break-words">
          {actionError.message}
        </AlertDescription>
      </Alert>
    );
  };
  const actionErrorAlert = errorAlertFor();

  return (
    <>
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Content Review</span>
            <Badge
              variant="outline"
              className={cn(status.bgColor, status.color, 'flex items-center gap-1')}
            >
              {status.icon}
              {status.label}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Metadata Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Version</span>
              <p className="font-medium">{metadata.version}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Updated</span>
              <p className="font-medium">{formatDate(metadata.updatedAt)}</p>
            </div>
            {metadata.createdByName && (
              <div>
                <span className="text-muted-foreground">Created By</span>
                <p className="font-medium flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {metadata.createdByName}
                </p>
              </div>
            )}
            {metadata.approvedByName && metadata.approvedAt && (
              <div>
                <span className="text-muted-foreground">Approved By</span>
                <p className="font-medium flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {metadata.approvedByName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(metadata.approvedAt)}
                </p>
              </div>
            )}
          </div>

          {/* Feedback Summary */}
          {feedbackSummary && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-2">Feedback Summary</h4>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <ThumbsUp className="h-4 w-4 text-green-600" />
                  {feedbackSummary.thumbsUp} positive
                </span>
                <span className="flex items-center gap-1">
                  <ThumbsDown className="h-4 w-4 text-red-600" />
                  {feedbackSummary.thumbsDown} negative
                </span>
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4 text-blue-600" />
                  {feedbackSummary.comments} comments
                </span>
              </div>
            </div>
          )}

          {/* Unresolved Issues Warning */}
          {hasUnresolvedIssues && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {feedbackSummary!.unresolved} unresolved issue(s) need attention before approval.
              </AlertDescription>
            </Alert>
          )}

          {/* Rejection Reason */}
          {metadata.status === 'rejected' && metadata.rejectionReason && (
            <Alert>
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Rejection reason:</strong> {metadata.rejectionReason}
              </AlertDescription>
            </Alert>
          )}

          {/* Failed status change — anchored to the buttons that caused it */}
          {actionErrorAlert}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 pt-2">
            {metadata.status === 'draft' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateStatus('pending_review')}
                disabled={isLoading}
              >
                <Send className="h-4 w-4 mr-2" />
                Submit for Review
              </Button>
            )}

            {(metadata.status === 'draft' || metadata.status === 'pending_review') && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setShowApproveDialog(true)}
                  disabled={isLoading || hasUnresolvedIssues}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>

                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowRejectDialog(true)}
                  disabled={isLoading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </>
            )}

            {metadata.status === 'rejected' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateStatus('draft')}
                disabled={isLoading}
              >
                <Clock className="h-4 w-4 mr-2" />
                Move to Draft
              </Button>
            )}

            {onRegenerate && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRegenerate}
                disabled={isLoading}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog
        open={showApproveDialog}
        onOpenChange={(open) => (open ? setShowApproveDialog(true) : closeDialogs('approve'))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Content</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this AEO content?
              Once approved, it will be available for export and may be synced to other systems.
            </DialogDescription>
          </DialogHeader>
          {errorAlertFor('approved')}
          <DialogFooter>
            <Button variant="outline" onClick={() => closeDialogs('approve')}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => updateStatus('approved')}
              disabled={isLoading}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={showRejectDialog}
        onOpenChange={(open) => (open ? setShowRejectDialog(true) : closeDialogs('reject'))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Content</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection. This will help the content creator understand what needs to be improved.
            </DialogDescription>
          </DialogHeader>
          {errorAlertFor('rejected')}
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => closeDialogs('reject')}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => updateStatus('rejected', rejectionReason)}
              disabled={isLoading || !rejectionReason.trim()}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AEOApprovalPanel;
